import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  fmtLbs,
  fmtPct,
  reasonLabel,
  PROBLEM_THRESHOLD_PCT,
} from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

const PAGE_SIZE = 15;

const reasonTagClass = (reason) => {
  if (reason === "contaminated") return "rl-reason-tag rl-reason-tag--contam";
  if (reason === "damaged") return "rl-reason-tag rl-reason-tag--damaged";
  if (reason === "expired") return "rl-reason-tag rl-reason-tag--expired";
  if (reason === "other") return "rl-reason-tag rl-reason-tag--other";
  return "rl-reason-tag";
};

const pctClass = (pct) => {
  if (pct >= PROBLEM_THRESHOLD_PCT) return "is-good";
  if (pct >= 85) return "is-warn";
  return "is-bad";
};

const ReceiverMaterialBalance = ({ rows = [] }) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState("acceptedPct");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!rows.length) return [];
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") {
        return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [rows, sortKey, sortDir]);

  const totals = useMemo(() => {
    let delivered = 0;
    let accepted = 0;
    for (const r of rows) {
      delivered += r.deliveredLbs || 0;
      accepted += r.acceptedLbs || 0;
    }
    return {
      delivered,
      accepted,
      rate: delivered > 0 ? Math.floor((accepted / delivered) * 1000) / 10 : 0,
    };
  }, [rows]);

  // pagination — pre-compute slice + bounds so we can clamp page on rows change
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, sorted.length);
  const pageRows = sorted.slice(pageStart, pageEnd);

  if (!rows.length) return null;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "material" ? "asc" : "desc");
    }
    setPage(0);
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return faSort;
    return sortDir === "asc" ? faSortUp : faSortDown;
  };

  const renderSortBtn = (col, label) => (
    <button
      type="button"
      className="mfca-sort-btn"
      onClick={() => handleSort(col)}
      aria-label={t("Sort by {{col}}", { col: label })}
    >
      {label}
      <FontAwesomeIcon icon={sortIcon(col)} className="mfca-sort-icon" />
    </button>
  );

  return (
    <div className="rl-table-wrapper">
      <table className="rl-data-table">
        <thead>
          <tr>
            <th>{renderSortBtn("material", t("Material"))}</th>
            <th className="mfca-col-num">{renderSortBtn("pickups", t("Pickups"))}</th>
            <th className="mfca-col-num">{renderSortBtn("deliveredLbs", t("Delivered"))}</th>
            <th className="mfca-col-num">{renderSortBtn("acceptedLbs", t("Accepted"))}</th>
            <th className="mfca-col-num">{renderSortBtn("acceptedPct", t("Rate"))}</th>
            <th>{t("Top reason")}</th>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const isProblem = (row.acceptedPct ?? 100) < PROBLEM_THRESHOLD_PCT;
            return (
              <tr key={row.material} className={isProblem ? "rl-row-problem" : ""}>
                <td>{row.material}</td>
                <td className="mfca-col-num">{row.pickups ?? "—"}</td>
                <td className="mfca-col-num">{fmtLbs(row.deliveredLbs)} {t("lbs")}</td>
                <td className="mfca-col-num">{fmtLbs(row.acceptedLbs ?? row.deliveredLbs)} {t("lbs")}</td>
                <td className={`mfca-col-num rl-pct rl-pct--${pctClass(row.acceptedPct ?? 100)}`}>
                  {fmtPct(row.acceptedPct ?? 100)}
                </td>
                <td>
                  {row.topRejectionReason ? (
                    <span className={reasonTagClass(row.topRejectionReason)}>
                      {t(reasonLabel(row.topRejectionReason))}
                    </span>
                  ) : (
                    <span className="rl-reason-empty">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>{t("Total")}</td>
            <td className="mfca-col-num rl-reason-empty">—</td>
            <td className="mfca-col-num">{fmtLbs(totals.delivered)} {t("lbs")}</td>
            <td className="mfca-col-num">{fmtLbs(totals.accepted)} {t("lbs")}</td>
            <td className="mfca-col-num">{fmtPct(totals.rate)}</td>
            <td className="rl-reason-empty">—</td>
          </tr>
        </tfoot>
      </table>

      <div className="rl-table-footer">
        <span className="rl-table-meta">
          {sorted.length > PAGE_SIZE
            ? t("Showing {{from}}–{{to}} of {{total}} materials", {
                from: pageStart + 1,
                to: pageEnd,
                total: sorted.length,
              })
            : t("Showing {{n}} of {{total}} materials", { n: pageRows.length, total: rows.length })}
        </span>

        {pageCount > 1 && (
          <div className="rl-table-pagination">
            <button
              type="button"
              className="rl-pagination-btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              aria-label={t("Previous page")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="rl-pagination-position">
              {safePage + 1} {t("of")} {pageCount}
            </span>
            <button
              type="button"
              className="rl-pagination-btn"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              aria-label={t("Next page")}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ReceiverMaterialBalance.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      material: PropTypes.string.isRequired,
      pickups: PropTypes.number,
      deliveredLbs: PropTypes.number,
      acceptedLbs: PropTypes.number,
      acceptedPct: PropTypes.number,
      rejectedLbs: PropTypes.number,
      topRejectionReason: PropTypes.string,
    })
  ),
};

export default ReceiverMaterialBalance;
